import { z } from 'zod/v4';

import { endpoint } from '../http/endpoint';
import { CalculationTemplatePostRuntype, CalculationTemplateRuntype } from './calculationTemplatesBackendTypes';

// Split into groups with an explicit type: the inferred one exceeds what TypeScript can serialize.
const getTemplatesEndpoint = {
    'GET /calculations/templates': endpoint({
        description: 'Fetches a list of calculation templates',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({
            data: z.array(CalculationTemplateRuntype),
        }),
        actionPermission: () => 'CanViewCalculationTemplate',
    }),
};

const getSingleTemplateEndpoint = {
    'GET /calculations/templates/:templateId': endpoint({
        description: 'Fetches a single calculation template',
        pathParams: z.object({
            templateId: z.string(),
        }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({
            data: CalculationTemplateRuntype,
        }),
        actionPermission: () => 'CanViewCalculationTemplate',
    }),
};

const deleteTemplateEndpoint = {
    'DELETE /calculations/templates/:templateId': endpoint({
        description: 'Deletes a calculation template',
        pathParams: z.object({ templateId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ deleted: z.number() }),
        invalidates: ['GET /calculations/templates', 'GET /calculations/templates/:templateId'],
        actionPermission: () => 'CanDeleteCalculationTemplate',
    }),
};

const createTemplateEndpoint = {
    'POST /calculations/templates': endpoint({
        description: 'Creates a new calculation template',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: CalculationTemplatePostRuntype,
        responseBody: z.object({
            data: CalculationTemplateRuntype,
        }),
        actionPermission: () => 'CanCreateCalculationTemplate',
    }),
};

const updateTemplateEndpoint = {
    'PATCH /calculations/templates/:templateId': endpoint({
        description: 'Edits a calculation template',
        pathParams: z.object({ templateId: z.string() }),
        queryParams: z.undefined(),
        requestBody: CalculationTemplatePostRuntype,
        responseBody: z.object({
            data: CalculationTemplateRuntype,
        }),
        actionPermission: () => 'CanUpdateCalculationTemplate',
    }),
};

type GetTemplatesEndpoint = typeof getTemplatesEndpoint;
type GetSingleTemplateEndpoint = typeof getSingleTemplateEndpoint;
type DeleteTemplateEndpoint = typeof deleteTemplateEndpoint;
type CreateTemplateEndpoint = typeof createTemplateEndpoint;
type UpdateTemplateEndpoint = typeof updateTemplateEndpoint;

interface CalculationTemplatesEndpoints
    extends
        GetTemplatesEndpoint,
        GetSingleTemplateEndpoint,
        DeleteTemplateEndpoint,
        CreateTemplateEndpoint,
        UpdateTemplateEndpoint {}

export const calculationTemplatesEndpoints: CalculationTemplatesEndpoints = {
    ...getTemplatesEndpoint,
    ...getSingleTemplateEndpoint,
    ...deleteTemplateEndpoint,
    ...createTemplateEndpoint,
    ...updateTemplateEndpoint,
};
